import { SeoPage } from './SeoLayout';
import { BUSINESS, getLocalBusinessSchema, getBreadcrumbSchema } from '../../data/businessInfo';
import { Phone, Mail, MapPin, Clock, Calendar } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function KontaktPage() {
  const contactSchema = {
    '@context': 'https://schema.org',
    '@type': 'ContactPage',
    url: `${BUSINESS.domain}/kontakt`,
    about: {
      '@type': 'HomeAndConstructionBusiness',
      name: BUSINESS.legalName,
      telephone: BUSINESS.phone,
      email: BUSINESS.email,
      address: {
        '@type': 'PostalAddress',
        streetAddress: BUSINESS.street,
        postalCode: BUSINESS.postalCode,
        addressLocality: BUSINESS.city,
        addressRegion: BUSINESS.region,
        addressCountry: BUSINESS.country
      },
      contactPoint: [{
        '@type': 'ContactPoint',
        telephone: BUSINESS.phone,
        contactType: 'customer service',
        areaServed: 'DE',
        availableLanguage: ['German', 'de']
      }]
    }
  };

  return (
    <SeoPage
      title="Kontakt | FarbWelt Maler Express Service Dormagen"
      description="Malerbetrieb aus Dormagen erreichen: Telefon 02133 7787688, E-Mail, Adresse, Kalender-Buchung. Antwort werktags meist am selben Tag."
      canonicalPath="/kontakt"
      kicker="Kontakt"
      heroTitle="So erreichen Sie uns"
      heroSubtitle="Am schnellsten telefonisch. Alternativ per E-Mail oder direkt über unseren Online-Kalender – Sie bekommen dann automatisch eine Bestätigung."
      jsonLd={[
        getLocalBusinessSchema(),
        contactSchema,
        getBreadcrumbSchema([
          { name: 'Startseite', item: '/' },
          { name: 'Kontakt', item: '/kontakt' }
        ])
      ]}
    >
      <div className="not-prose grid sm:grid-cols-2 gap-4 my-6">
        <a href={`tel:${BUSINESS.phone}`} className="p-6 bg-white border border-gray-200 rounded-xl hover:border-[#9B1B30] hover:shadow-lg transition-all group">
          <Phone className="w-6 h-6 text-[#9B1B30] mb-3" />
          <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Telefon</div>
          <div className="text-2xl font-bold text-[#1A1A1A] group-hover:text-[#9B1B30] transition-colors">{BUSINESS.phoneDisplay}</div>
          <div className="text-sm text-gray-600 mt-2">Werktags am schnellsten</div>
        </a>

        <a href={`mailto:${BUSINESS.email}`} className="p-6 bg-white border border-gray-200 rounded-xl hover:border-[#9B1B30] hover:shadow-lg transition-all group">
          <Mail className="w-6 h-6 text-[#9B1B30] mb-3" />
          <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">E-Mail</div>
          <div className="text-lg font-bold text-[#1A1A1A] group-hover:text-[#9B1B30] transition-colors break-all">{BUSINESS.email}</div>
          <div className="text-sm text-gray-600 mt-2">Antwort meist innerhalb eines Werktags</div>
        </a>

        <Link to="/termin" className="p-6 bg-[#9B1B30] rounded-xl hover:bg-[#7a1526] transition-all group text-white">
          <Calendar className="w-6 h-6 mb-3" />
          <div className="text-xs uppercase tracking-wide mb-1 opacity-90">Online-Termin</div>
          <div className="text-2xl font-bold">Kalender öffnen</div>
          <div className="text-sm mt-2 opacity-90">Kostenlose Besichtigung buchen</div>
        </Link>

        <div className="p-6 bg-white border border-gray-200 rounded-xl">
          <MapPin className="w-6 h-6 text-[#9B1B30] mb-3" />
          <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Anschrift</div>
          <div className="text-lg font-semibold text-[#1A1A1A]">{BUSINESS.legalName}</div>
          <div className="text-gray-700">{BUSINESS.street}</div>
          <div className="text-gray-700">{BUSINESS.postalCode} {BUSINESS.city}</div>
        </div>
      </div>

      <h2>Wann Sie uns am besten erreichen</h2>
      <p>
        Wir sind ein Handwerks­betrieb — tagsüber häufig auf Baustellen. Am zuverlässigsten
        erreichen Sie uns telefonisch vormittags oder am frühen Abend. Wenn Sie
        die Mailbox erreichen: kurz Namen und Rückrufnummer sprechen, wir rufen
        werktags in der Regel am selben Tag zurück.
      </p>

      <h2>Was hilft uns beim ersten Kontakt?</h2>
      <ul>
        <li>Ihre Adresse und Region (damit wir Fahrtwege einschätzen können)</li>
        <li>Um welche Räume es geht (Anzahl, ungefähre Größe)</li>
        <li>Was gemacht werden soll (Anstrich, Tapete, Übergabe, Renovierung usw.)</li>
        <li>Ihr gewünschter Zeitrahmen (asap, in 2 Wochen, im nächsten Monat …)</li>
      </ul>

      <h2>Firmendaten</h2>
      <table className="not-prose w-full text-sm border-collapse my-4">
        <tbody>
          <tr className="border-b"><td className="py-2 pr-4 text-gray-500">Firmierung</td><td className="py-2 font-medium">{BUSINESS.legalName}</td></tr>
          <tr className="border-b"><td className="py-2 pr-4 text-gray-500">Sitz</td><td className="py-2">{BUSINESS.street}, {BUSINESS.postalCode} {BUSINESS.city}</td></tr>
          <tr className="border-b"><td className="py-2 pr-4 text-gray-500">Handwerkskammer</td><td className="py-2">{BUSINESS.chamber}</td></tr>
          <tr className="border-b"><td className="py-2 pr-4 text-gray-500">Steuernummer</td><td className="py-2 font-mono">{BUSINESS.taxNumber}</td></tr>
          <tr><td className="py-2 pr-4 text-gray-500">USt-IdNr.</td><td className="py-2 font-mono">{BUSINESS.vatId}</td></tr>
        </tbody>
      </table>

      <p className="text-sm text-gray-500">
        Rechtliches: <Link to="/impressum">Impressum</Link> · <Link to="/datenschutz">Datenschutz</Link> · <Link to="/agb">AGB</Link>
      </p>
    </SeoPage>
  );
}
